<template>
  <div
    v-if="!camera.liveFeedUrl"
    class="text-background-fg bg-background-fg/5 flex h-full w-full items-center justify-center p-4"
  >
    <p>No live feed set for this camera</p>
  </div>
  <div v-else class="relative h-full w-full">
    <BaseContainerLoading v-if="loading" />
    <div
      v-else-if="error"
      class="text-background-fg bg-background-fg/5 flex h-full w-full items-center justify-center p-4"
    >
      <p>Failed to load camera stream</p>
    </div>
    <img
      v-if="streamSrc"
      ref="imgRef"
      :src="streamSrc"
      class="h-full w-full object-cover"
      :class="{ invisible: loading || error }"
      @load="onLoad"
      @error="onError"
    />
  </div>
</template>

<script setup lang="ts">
import { type Camera } from "@/services/cameras/listCameras";
import { getCameraStreamUrl } from "@/services/cameras/getCameraStreamToken";

const props = defineProps<{ camera: Camera }>();

const imgRef = ref<HTMLImageElement>();
const loading = ref(true);
const error = ref(false);
const streamSrc = ref<string>();

watch(
  () => props.camera.id,
  async (cameraId) => {
    // Unmounts the img. Clearing its src first closes the old stream; the
    // `error` that fires for the empty src is ignored by onError.
    if (imgRef.value) {
      imgRef.value.src = "";
    }
    streamSrc.value = undefined;
    loading.value = true;
    error.value = false;
    if (!props.camera.liveFeedUrl) {
      return;
    }
    try {
      const url = await getCameraStreamUrl(cameraId);
      // A newer camera was picked while the token was in flight.
      if (props.camera.id === cameraId) {
        streamSrc.value = url;
      }
    } catch {
      loading.value = false;
      error.value = true;
    }
  },
  { immediate: true },
);

// Events from an img that is no longer the current stream (its src was
// cleared on a camera switch or after an error) are stale: they fire after the
// token for the next stream has been requested and would replace the spinner.
const isCurrentImg = (event: Event) =>
  streamSrc.value !== undefined && event.target === imgRef.value;

function onLoad(event: Event) {
  if (!isCurrentImg(event)) {
    return;
  }
  loading.value = false;
  error.value = false;
}

function onError(event: Event) {
  if (!isCurrentImg(event)) {
    return;
  }
  loading.value = false;
  error.value = true;
  const img = imgRef.value;
  streamSrc.value = undefined;
  if (img) {
    img.src = "";
  }
}

onBeforeUnmount(() => {
  if (imgRef.value) {
    imgRef.value.src = "";
  }
});
</script>
